var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
var _a, _b;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Box, Button, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableRow, Typography, } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { PayloadMismatchError, checkDocument, documentKindOf, } from "@/lib/signing/request";
import { DocumentKind, SigningAction } from "@/lib/signing/types";
import { actionObject, documentTypeLabel, shortHash } from "./format";
import { problemMessage, useSignedView } from "./useSignedView";
import { ConfigurationAuthorizes, LIFECYCLE_SUBJECT_KEYS } from "./ConfigurationAuthorizes";
export var SigningSubjectVariant;
(function (SigningSubjectVariant) {
    SigningSubjectVariant["Panel"] = "panel";
    SigningSubjectVariant["Dialog"] = "dialog";
})(SigningSubjectVariant || (SigningSubjectVariant = {}));
var MIME_TYPE = (_a = {},
    _a[DocumentKind.Pdf] = "application/pdf",
    _a[DocumentKind.Eml] = "application/xml",
    _a[DocumentKind.NoDocument] = "application/octet-stream",
    _a);
/** A blob URL outlives its tab only briefly: long enough for the viewer to load it. */
var BLOB_URL_LIFETIME_MS = 60000;
/**
 * "Open the document": downloads it, checks it against the hash the signed
 * payload names, and shows those verified bytes; a different document is refused.
 */
var DocumentCard = function (_a) {
    var _b, _c;
    var data = _a.data, view = _a.view, api = _a.api;
    var t = useTranslation().t;
    var kind = documentKindOf(data.request);
    var hash = (_b = view.documentSha256) !== null && _b !== void 0 ? _b : "";
    var _d = useState(false), opening = _d[0], setOpening = _d[1];
    var _e = useState(null), error = _e[0], setError = _e[1];
    var openDocument = function () { return __awaiter(void 0, void 0, void 0, function () {
        var bytes, url_1, failure_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!data.document_url)
                        return [2 /*return*/];
                    setOpening(true);
                    setError(null);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, api.fetchDocument(data.document_url)];
                case 2:
                    bytes = _a.sent();
                    return [4 /*yield*/, checkDocument(bytes, view)];
                case 3:
                    _a.sent();
                    url_1 = URL.createObjectURL(new Blob([bytes], { type: MIME_TYPE[kind] }));
                    window.open(url_1, "_blank", "noopener");
                    setTimeout(function () { return URL.revokeObjectURL(url_1); }, BLOB_URL_LIFETIME_MS);
                    return [3 /*break*/, 6];
                case 4:
                    failure_1 = _a.sent();
                    setError(failure_1 instanceof PayloadMismatchError
                        ? "signing.widget.documentMismatch"
                        : "signing.widget.documentError");
                    return [3 /*break*/, 6];
                case 5:
                    setOpening(false);
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Paper, { variant: "outlined", sx: {
            p: 2,
            display: "grid",
            gridTemplateColumns: "auto minmax(0, 1fr)",
            columnGap: 2,
            rowGap: 1,
            alignItems: "center",
        }, "data-testid": "signing-document", children: [_jsx(DescriptionOutlinedIcon, { color: "action", "aria-hidden": true }), _jsxs(Box, { sx: { minWidth: 0 }, children: [_jsx(Typography, { sx: { fontWeight: 600, overflowWrap: "anywhere", m: 0 }, children: (_c = data.document_name) !== null && _c !== void 0 ? _c : actionObject(t, data.request.action) }), _jsx(Typography, { variant: "body2", color: "text.secondary", title: hash, sx: { m: 0, mt: 0.5 }, children: data.document_pages
                            ? t("signing.widget.documentPages", {
                                type: documentTypeLabel(kind),
                                pages: data.document_pages,
                                hash: shortHash(hash),
                            })
                            : t("signing.widget.document", {
                                type: documentTypeLabel(kind),
                                hash: shortHash(hash),
                            }) })] }), data.document_url ? (_jsx(Button, { variant: "secondary", size: "small", sx: {
                    px: 1.5,
                    py: 0.5,
                    gridColumn: { xs: "1 / -1", sm: 2 },
                    justifySelf: "start",
                    minHeight: { xs: 44, sm: 36 },
                    fontSize: "0.875rem",
                    fontWeight: 600,
                }, endIcon: _jsx(OpenInNewIcon, { fontSize: "small" }), disabled: opening, startIcon: opening ? _jsx(CircularProgress, { size: 14, "aria-hidden": true }) : undefined, onClick: function () {
                    openDocument().catch(function () { return undefined; });
                }, children: t("signing.panel.openDocument") })) : null, error ? (_jsx(Alert, { severity: "error", sx: { gridColumn: "1 / -1" }, children: t(error) })) : null] }));
};
/** The subject field of open and close voting with each channel's status before. */
var FROM_KEY = "from";
/** The subject field of a configuration version with the signing rules it changes. */
var SIGNING_RULES_KEY = "signing_rules";
/** A rule's value in a `signing_rules` entry when it needs no signatures. */
var RULE_OFF = "off";
/**
 * A `signing_rules` entry, `ACTION=BEFORE>AFTER` (or `ACTION=AFTER` when what
 * it was is unknown; `ACTION` alone in requests started before entries carried
 * numbers), each value `off` or the signatures needed: "Close voting: needs 2
 * (was 1)".
 */
var ruleChange = function (t, code) {
    var _a, _b;
    var separator = code.indexOf("=");
    var action = separator > 0 ? code.slice(0, separator) : code;
    var name = t("signing.values.signing_rules.".concat(action), { defaultValue: action });
    if (separator <= 0)
        return name;
    var _c = code.slice(separator + 1).split(">", 2), before = _c[0], after = _c[1];
    var off = function (value) { return (value === RULE_OFF ? t("signing.values.ruleOff") : null); };
    var current = after !== null && after !== void 0 ? after : before;
    var now = (_a = off(current)) !== null && _a !== void 0 ? _a : t("signing.values.ruleNeeds", { n: current });
    return after !== undefined && after !== before
        ? t("signing.values.ruleChangeFrom", { action: name, rule: now, was: (_b = off(before)) !== null && _b !== void 0 ? _b : before })
        : t("signing.values.ruleChange", { action: name, rule: now });
};
/**
 * A signed value in the organization's words: each code under
 * `signing.values.<key>.<code>`, else the code as signed. A `from` entry
 * reads `CHANNEL=STATUS`: "Online: Open".
 */
export var worded = function (t, key, raw, shown) {
    var word = function (code) {
        if (typeof code !== "string")
            return String(code);
        if (key === SIGNING_RULES_KEY)
            return ruleChange(t, code);
        var separator = code.indexOf("=");
        if (key === FROM_KEY && separator > 0) {
            var channel = code.slice(0, separator);
            var status_1 = code.slice(separator + 1);
            return t("signing.values.channelStatus", {
                channel: t("signing.values.channels.".concat(channel), { defaultValue: channel }),
                status: t("signing.values.statuses.".concat(status_1), { defaultValue: status_1 }),
            });
        }
        return t("signing.values.".concat(key, ".").concat(code), { defaultValue: code });
    };
    if (Array.isArray(raw))
        return raw.map(word).join(", ");
    return typeof raw === "string" ? word(raw) : shown;
};
/** The keys of each action's notes, under `signing.notes`. */
var NOTES = (_b = {},
    _b[SigningAction.ApproveVoter] = [["afterApproval", "afterApprovalValue"]],
    _b[SigningAction.ConfirmCustodyProof] = [
        ["custodyProof", "custodyProofChecked"],
        ["recordedIn", "recordedInCeremony"],
    ],
    _b[SigningAction.ContributeCustodyProof] = [
        ["custodyProof", "custodyProofChecked"],
        ["recordedIn", "recordedInTally"],
    ],
    _b);
/** What the details table adds after the signed rows of `action` (drafts Other staff 1 and 3). */
export var subjectNotes = function (t, action) {
    var _a;
    return ((_a = NOTES[action]) !== null && _a !== void 0 ? _a : []).map(function (_a) {
        var label = _a[0], value = _a[1];
        return ({
            label: t("signing.notes.".concat(label)),
            value: t("signing.notes.".concat(value)),
        });
    });
};
/**
 * What is signed, read from the canonical payload the approval signs: the
 * document card (name, type, pages, SHA-256), or the details table for an
 * action without a document; then the signing code everyone who signs
 * compares. A payload that doesn't describe the request is refused here.
 */
export var SigningSubject = function (_a) {
    var _b;
    var data = _a.data, api = _a.api, _c = _a.variant, variant = _c === void 0 ? SigningSubjectVariant.Panel : _c;
    var t = useTranslation().t;
    var _d = useSignedView(data), view = _d.view, problem = _d.problem;
    var kind = documentKindOf(data.request);
    var notes = subjectNotes(t, data.request.action);
    // A configuration approval shows its lifecycle snapshot in words, not as rows.
    var configuration = data.request.action === SigningAction.ApproveConfiguration;
    var rows = ((_b = view === null || view === void 0 ? void 0 : view.rows) !== null && _b !== void 0 ? _b : []).filter(function (row) { return !configuration || !LIFECYCLE_SUBJECT_KEYS.includes(row.key); });
    return (_jsxs(Stack, { spacing: 2, children: [problem ? (_jsx(Alert, { severity: "error", "data-testid": "signing-mismatch", children: t(problemMessage(problem)) })) : null, view && kind !== DocumentKind.NoDocument ? (_jsx(DocumentCard, { data: data, view: view, api: api })) : view && (rows.length || notes.length) ? (_jsxs(Box, { children: [data.request.action === SigningAction.ApproveConfiguration ? (_jsx(Typography, { variant: "subtitle2", component: "h3", sx: { mb: 1 }, children: t("signing.panel.configurationChanges") })) : null, _jsx(Table, { size: "small", "aria-label": t("signing.widget.panel.details"), children: _jsxs(TableBody, { children: [rows.map(function (row) {
                                    var _a;
                                    return (_jsxs(TableRow, { children: [_jsx(TableCell, { component: "th", scope: "row", sx: { color: "text.secondary", width: "40%" }, children: t("signing.details.".concat(row.key), {
                                                    defaultValue: (_a = row.label) !== null && _a !== void 0 ? _a : row.key,
                                                }) }), _jsx(TableCell, { sx: { overflowWrap: "anywhere" }, children: row.name ? (_jsxs(_Fragment, { children: [row.name, _jsx(Typography, { variant: "body2", color: "text.secondary", component: "div", children: row.value })] })) : (worded(t, row.key, view.subject[row.key], row.value)) })] }, row.key));
                                }), notes.map(function (note) { return (_jsxs(TableRow, { "data-testid": "signing-note", children: [_jsx(TableCell, { component: "th", scope: "row", sx: { color: "text.secondary", width: "40%" }, children: note.label }), _jsx(TableCell, { children: note.value })] }, note.label)); })] }) })] })) : null, view && configuration ? (_jsx(ConfigurationAuthorizes, { subject: view.subject, electionEventId: data.request.election_event_id, requestId: data.request.id })) : null, _jsxs(Stack, { direction: { xs: "column", sm: "row" }, spacing: 1, sx: { alignItems: { sm: "baseline" }, justifyContent: "space-between" }, children: [_jsxs(Typography, { component: "div", children: [t("signing.panel.signingCode"), " ", _jsx(Box, { component: "span", "data-testid": "signing-code", sx: { fontFamily: "monospace", fontSize: "1.2rem", fontWeight: 700, ml: 1 }, children: data.request.code })] }), variant === SigningSubjectVariant.Dialog ? (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("signing.dialog.check.sameCode") })) : null] })] }));
};
